import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LAUNCH_CAMPAIGN_WORKFLOW } from '../../constants/automationActions';
import AutomationStepList from './AutomationStepList';
import LaunchCampaignParams from './LaunchCampaignParams';
import { humanize, triggerSummary } from './automationFormat';

const AUTOMATION_WARNINGS = {
  activate_campaign: 'Approval starts this campaign sequence for the selected accounts.',
  execute_workflow: 'Approval runs this workflow now for the selected accounts.',
};

const ACTIVATION_WARNINGS = {
  set_workflow_status: 'Activation can run this workflow for its full configured audience.',
  set_campaign_status: 'Resuming continues outbound steps for enrolled accounts.',
};

const TARGET_LABELS = {
  template_id: 'Campaign',
  instance_id: 'Campaign',
  workflow_id: 'Workflow',
};

function warningFor(actionType, params) {
  if (params.status === 'active' && ACTIVATION_WARNINGS[actionType]) {
    return ACTIVATION_WARNINGS[actionType];
  }
  return AUTOMATION_WARNINGS[actionType];
}

function buildRows(params, action) {
  const targetKey = Object.keys(TARGET_LABELS).find((key) => params[key]);
  const rows = [];
  if (targetKey) {
    rows.push([TARGET_LABELS[targetKey], action.target_name || params[targetKey]]);
  } else if (params.name) {
    rows.push(['Name', params.name]);
  }
  if (params.account_ids?.length) {
    const names = action.account_names?.length ? action.account_names : params.account_ids;
    rows.push([names.length === 1 ? 'Account' : `Accounts (${names.length})`, names.join(', ')]);
  }
  if (params.status) rows.push(['New Status', humanize(params.status)]);
  if (targetKey && params.name) rows.push(['New Name', params.name]);
  if (params.goal) rows.push(['Goal', params.goal]);
  if (params.target_segment) rows.push(['Segment', humanize(params.target_segment)]);
  if (params.trigger_type) rows.push(['Trigger', humanize(params.trigger_type)]);
  const conditions = triggerSummary(params.trigger_type, params.trigger_config);
  if (conditions) rows.push(['Conditions', conditions]);
  if (params.description) rows.push(['Description', params.description, true]);
  return rows;
}

/** Readable approval details for campaign and workflow operations. */
export default function AutomationActionParams({ actionType, params = {}, action = {} }) {
  if (actionType === LAUNCH_CAMPAIGN_WORKFLOW) {
    return <LaunchCampaignParams params={params} action={action} />;
  }
  const warning = warningFor(actionType, params);
  const sequence = params.steps || params.actions || [];

  return (
    <div className="mt-2 space-y-2 text-xs">
      {warning && (
        <div
          role="note"
          className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{warning}</span>
        </div>
      )}
      <div className="space-y-1">
        {buildRows(params, action).map(([label, value, multiline]) => (
          <div key={label} className={cn('flex gap-2', multiline && 'flex-col gap-0.5')}>
            <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
            <span
              className={cn(
                'min-w-0 break-words text-foreground',
                multiline && 'line-clamp-3 whitespace-pre-wrap',
              )}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
      <AutomationStepList title={params.steps ? 'Steps' : 'Actions'} items={sequence} />
    </div>
  );
}
